import React, { useState, useEffect } from 'react';
import CommissionOverviewPanel, { ICommissionsOverviewMetrics } from './components/CommissionOverviewPanel';
import CommissionTrendsChart, { ICommissionsMonthlyTrend } from './components/CommissionTrendsChart';
import CommissionDistributionCharts, { ICommissionsDistributionData } from './components/CommissionDistributionCharts';
import usePartner from '../../../../hooks/partnerHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllCommissionChartDataByCompanyId, getAllCommissions, getCommissionDistributionByInsurer, getCommissionDistributionByPolicyTypeByCompanyId, getCommissionMetricsByCompanyId, getMonthlyCommissionTrends, markInsurerPayoutAsPaid } from '../../../../api/requests';
import useUserAccount from '../../../../hooks/userAccountHook';
import Page from '../../../../components/PageWrapper';
import { COMPANY_TYPES } from '../../../../resources/enums';
import { Commission } from '../../../../models/commission/Commission';
import { ITablePaginationData } from '../../../../components/elements/GTable/GTable';
import CommissionsTable from '../../shared/commissions/CommissionsTable';
import TabNav, { ITab } from '../../../../components/tabs/TabNav';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import { capitalizeSentence, onRemoveFilter, removeUnderscoresAndCapitalize } from '../../../../utils/miscFunctions';
import SettlementsTable from '../../shared/settlements/components/SettlementsTable';
import TableFilter from '../../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { IEnum, ITableFilterItem } from '../../../../resources/interfaces';
import { getEnumsFromState } from '../../../../states/slices/resourceSlice';
import { useSelector } from 'react-redux';
import { Company } from '../../../../models/company/Company';

interface ICommissionsChartData {
  insurerDistribution: {
    title: string,
    items: any[],
    total: number,
    currency: string
  },
  monthlyTrend: {
    months: string[],
    earnedAmounts: number[],
    paidAmounts: number[],
    currency: string
  },
  metrics: {
    totalAmount: number,
    paidAmount: number,
    pendingAmount: number,
    disputedAmount: number,
    totalCount: number,
    paidCount: number,
    pendingCount: number,
    disputedCount: number,
    currency: string
  },
  protectionTypeDistribution: {
    title: string,
    items: any[],
    total: number,
    currency: string
  }
}

const CommissionsPage: React.FC = () => {
  const { user } = useUserAccount()
  const { fetchAllCompaniesByType } = usePartner();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { getDashboardBase } = useRoute();
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'statistics');
  const enums = useSelector(getEnumsFromState);
  const [commissionsPaginationData, setCommissionsPaginationData] = useState<ITablePaginationData>();
  const [searchString, setSearchString] = useState<string>('');
  const queryClient = useQueryClient()
  const [clickedMetricCard, setClickedMetricCard] = useState<boolean | null | undefined>(undefined);
  const [isMetricsloading, setIsMetricsloading] = useState<boolean>(true);

  const handleMetricCardClick = (isPaid: boolean | null) => {
    setClickedMetricCard(isPaid);
    setActiveTab('transactions');
    setSearchParams({ tab: 'transactions' });

    setTableFilters((prevFilters = []) => {
      // Remove both isPaid and payment_status filters if they exist
      const otherFilters = prevFilters.filter(
        filter => filter.name !== 'isPaid' && filter.name !== 'payment_status'
      );

      // If isPaid is not null, add the payment_status filter
      if (isPaid !== null) {
        return [
          ...otherFilters,
          {
            name: 'payment_status',  // Changed from 'isPaid' to 'payment_status'
            value: isPaid,
            value2: isPaid ? 'Paid' : 'Not paid',
            type: 'select',
            placeholder: 'Select payment status',
            label: capitalizeSentence('Payment status'),
            list: [
              { name: 'Paid', value: true },
              { name: 'Unpaid', value: false },
            ],
          }
        ];
      }

      return otherFilters;
    });
  };

  const [monthlyCommissionTrendsData, setMonthlyCommissionTrendsData] = useState<ICommissionsMonthlyTrend>({
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    earnedAmounts: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    paidAmounts: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    currency: "GHS"
  });

  const [policyTypeDistribution, setPolicyTypeDistribution] = useState<ICommissionsDistributionData>({
    currency: "GHS",
    items: [],
    title: "",
    total: 0
  });

  const [insurerDistribution, setInsurerDistribution] = useState<ICommissionsDistributionData>({
    currency: "GHS",
    items: [],
    title: "",
    total: 0
  });

  const [commissionOverviewMetricsData, setCommissionOverviewMetricsData] = useState<ICommissionsOverviewMetrics>({
    currency: "GHS",
    disputedAmount: 0,
    disputedCount: 0,
    disputedGross: 0,
    disputedWTH: 0,
    expectedAmount: 0,
    expectedCount: 0,
    expectedGross: 0,
    expectedWTH: 0,
    paidAmount: 0,
    paidCount: 0,
    paidGross: 0,
    paidWTH: 0,
    pendingAmount: 0,
    pendingCount: 0,
    pendingGross: 0,
    pendingWTH: 0,
    receivedAmount: 0,
    receivedCount: 0,
    receivedGross: 0,
    receivedWTH: 0,
    totalAmount: 0,
    totalCount: 0,
    totalGross: 0,
    totalWHT: 0,
    isMetricLoading: isMetricsloading,
  });

  const [allCommissionChartData, setAllCommissionChartData] = useState<ICommissionsChartData>({
    insurerDistribution: {
      title: "",
      items: [],
      total: 0,
      currency: ""
    },
    monthlyTrend: {
      months: [],
      earnedAmounts: [],
      paidAmounts: [],
      currency: ""
    },
    metrics: {
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
      disputedAmount: 0,
      totalCount: 0,
      paidCount: 0,
      pendingCount: 0,
      disputedCount: 0,
      currency: ""
    },
    protectionTypeDistribution: {
      title: "",
      items: [],
      total: 0,
      currency: ""
    }
  });

  const { data: brokers } = useQuery({
    queryKey: ['fetchBrokers',],
    queryFn: async () => {
      const res = await fetchAllCompaniesByType(COMPANY_TYPES.BROKER)
      return res
    },
    enabled: !!(user.isInternalAdmin())
  })

  const { data: insurers } = useQuery({
    queryKey: ['fetchInsurers',],
    queryFn: async () => {
      const res = await fetchAllCompaniesByType(COMPANY_TYPES.INSURER)
      return res
    },
  })

  const toDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.to;
  const fromDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.from;
  const brokerId = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'broker_id')?.value || (user.isBroker() && user?.company?.id) || (brokers && brokers[0].id);
  const insurerId = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'insurer_id')?.value || (user.isInsurer() && user?.company?.id) || null;
  const policyType = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'policy_type')?.value
  const isPaid = clickedMetricCard ? clickedMetricCard : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'payment_status')?.value;

  const isApproved = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'approved_status')?.value;
  const reviewStage = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'review_stage')?.value;

  const fetchCommissionDistributionByPolicyType = async () => {
    try {
      const res: any = await getCommissionDistributionByPolicyTypeByCompanyId(Number(brokerId));
      if (res) {
        setPolicyTypeDistribution(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const fetchCommissionMetricsByCompanyId = async () => {
    setIsMetricsloading(true);
    try {
      const res: any = await getCommissionMetricsByCompanyId(Number(brokerId), Number(insurerId), policyType, fromDate, toDate);
      if (res) {
        setCommissionOverviewMetricsData(res?.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsMetricsloading(false);
    }
  }

  const fetchAllCommissionChartData = async () => {
    try {
      const res: any = await getAllCommissionChartDataByCompanyId(Number(brokerId), fromDate, toDate);
      if (res) {
        setAllCommissionChartData(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const fetchMonthlyCommissionTrends = async () => {
    try {
      const res: any = await getMonthlyCommissionTrends(Number(brokerId), new Date().getFullYear());
      if (res) {
        setMonthlyCommissionTrendsData(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const fetchCommissionDistributionByInsurer = async () => {
    try {
      const res: any = await getCommissionDistributionByInsurer(Number(brokerId));
      if (res) {
        setInsurerDistribution(res?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }


  const fetchAllCommissions = () => {
    const paymentStatusFilter = tableFilters?.find(f => f.name === 'payment_status')?.value;
    const statusValue = paymentStatusFilter ? 'true' : paymentStatusFilter === false ? 'false' : undefined;

    getAllCommissions(currentPage, showPerPage, Number(brokerId), policyType, insurerId, searchString, fromDate, toDate, statusValue)
      .then((response: any) => {
        setCommissions(response?.content?.map((commission: any) => new Commission(commission)))
        const from = response?.pageable?.offset + 1;
        const to =
          from + response?.size > response?.totalElements
            ? response?.totalElements
            : from + response?.size - 1;
        setCommissionsPaginationData({
          per_page: response?.size,
          total: response?.totalElements,
          totalPages: response?.totalPages,
          from: from,
          to: to
        });
      })
      .catch((error: any) => {
        console.error('Error fetching commissions:', error);
      });
  };

  function getDefaultFilters(): ITableFilterItem[] {
    const baseFilters: ITableFilterItem[] = [
      {
        name: 'insurer_id',
        value: '',
        type: 'select',
        placeholder: 'Select insurer',
        label: capitalizeSentence('Insurer'),
        list: insurers?.map((insurer: Company) => ({
          name: insurer.name,
          value: insurer.id,
        })),
        showSearch: true
      },
      {
        name: 'create_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select date range',
        label: capitalizeSentence('Date range'),
      },
      {
        name: 'policy_type',
        value: '',
        type: 'select',
        placeholder: 'Select policy type',
        label: capitalizeSentence('Policy type'),
        list: enums?.policyTypes?.map((type: IEnum) => ({
          name: removeUnderscoresAndCapitalize(type.title),
          value: type.key,
        })),
      },
      {
        name: 'payment_status',
        value: '',
        value2:'',
        type: 'select',
        placeholder: 'Select payment status',
        label: 'Payment Status',
        list: [{
          name: 'Paid',
          value: 'true',
        }, {
          name: 'Unpaid',
          value: 'false',
        }],
      }
    ];

    const internalOwnerFilters: ITableFilterItem[] = [
      {
        name: 'broker_id',
        value: '',
        type: 'select',
        placeholder: 'Select broker',
        label: capitalizeSentence('Broker'),
        list: brokers?.map((broker: any) => ({
          name: broker.name,
          value: broker.id,
        })),
      },
      {
        name: 'business_class',
        value: '',
        type: 'select',
        placeholder: 'Select business class',
        label: capitalizeSentence('Business class'),
        list: enums?.portfolio?.map((item: any) => ({
          name: item.name,
          value: item.id,
        })),
      },
    ];

    // Build filters dynamically
    let filters = [...baseFilters];
    if (user?.isInternalOwner()) {
      filters.unshift(...internalOwnerFilters);
    }

    return filters;
  }

  const { isLoading: commissionsLoading } = useQuery({
    queryKey: ['commissions', brokerId, currentPage, showPerPage, policyType, insurerId, toDate, fromDate, searchString, isPaid],
    queryFn: fetchAllCommissions,
  });

  useEffect(() => {
    setTableFilters(getDefaultFilters());
  }, [insurers, brokers,]);

  useQuery({
    queryKey: ['fetchCommissionDistributionByInsurer', brokerId, insurerId, policyType, toDate, fromDate],
    queryFn: fetchCommissionDistributionByInsurer,

  })

  useQuery({
    queryKey: ['fetchCommissionDistributionByPolicyType', brokerId, insurerId, policyType, toDate, fromDate],
    queryFn: fetchCommissionDistributionByPolicyType,
  })

  useQuery({
    queryKey: ['fetchAllCommissionChartData', brokerId, insurerId, policyType, toDate, fromDate],
    queryFn: fetchAllCommissionChartData,
  })

  useQuery({
    queryKey: ['fetchCommissionMetricsByCompanyId', brokerId, insurerId, policyType, toDate, fromDate],
    queryFn: fetchCommissionMetricsByCompanyId,
  })

  useQuery({
    queryKey: ['fetchMonthlyCommissionTrends', brokerId],
    queryFn: fetchMonthlyCommissionTrends,

  })

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Commissions | Brolly Portal';
  }, []);

  const getTabs = () => {
    let tabs: ITab[] = [
      {
        id: 'statistics',
        title: 'Statistics'
      },
      {
        id: 'transactions',
        title: 'Transactions'
      },
      {
        id: 'settlements',
        title: 'Settlements'
      }
    ];

    return tabs;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
  };

  const getFilter = () => {
    return (
      <div className="flex flex-col items-center gap-2 max-w-[10rem]">
        <TableFilter
          openFilter={openFilter}
          onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
          selectedFilters={tableFilters}
          content={
            <TableFilterForm
              filters={tableFilters}
              getValues={(filters: ITableFilterItem[]) => {
                setTableFilters(filters);
                setOpenFilter(false);

              }}
              onResetFilters={() => setTableFilters(getDefaultFilters())}
            />
          }
        />
      </div>
    )
  }

  return (
    <Page title="Approvals">
      <div className="bg-white dark:bg-darkBg w-full p-5 min-h-full  rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-darkBg-mainText dark:to-darkBg-subText">Sales and Commissions</h1>
            </div>
            <p className="text-gray-600 dark:text-darkBg-subText ml-4 pl-2 border-l-2 border-gray-200 dark:border-darkBg-border">Manage and track your commission earnings</p>
          </div>

          {/* Filters & Actions */}
          <div className="flex items-center space-x-3">
            {getFilter()}
          </div>
        </div>

        {/* Summary Stats */}
        <CommissionOverviewPanel data={commissionOverviewMetricsData} onCardClick={(cardTitle: boolean | null) => handleMetricCardClick(cardTitle)} />

        {/* Tabs */}
        <div className="mt-8">
          <TabNav
            tabs={getTabs()}
            activeTab={activeTab}
            setActiveTab={(id: string) => {
              navigate(`${getDashboardBase()}/commissions?tab=${id}`);
              setActiveTab(id);
            }}
            classes='mb-6 border-b'
          />

          {/* Statistics Tab */}
          {activeTab === 'statistics' && (
            <div className="space-y-6">
              <CommissionTrendsChart data={monthlyCommissionTrendsData} />

              <CommissionDistributionCharts insurerDistributionData={insurerDistribution} policyTypeDistributionData={policyTypeDistribution} />
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <CommissionsTable
              commissions={commissions}
              loading={commissionsLoading}
              showTitleSection={true}
              paginationData={commissionsPaginationData}
              showPerPage={showPerPage}
              onPageChange={handlePageChange}
              onShowPerPageChange={(page: number) => setShowPerPage(page)}
              onSearchChange={(search: string) => setSearchString(search)}
              onRemoveFilter={(filter: ITableFilterItem, index: number) => onRemoveFilter(filter, index, tableFilters || [], setTableFilters)}
              selectedFilters={tableFilters || []}
              // onRefreshCommissions={() => invalidateQueries()}
            />
          )}

          {/* Settlements Tab */}
          {activeTab === 'settlements' && (
            <SettlementsTable
              showTitleSection={true}
              selectedBrokerFromParent={brokerId}
              selectedInsurerFromParent={insurerId}
              fromDate={fromDate}
              toDate={toDate}
              isPaid={isPaid}
              isApproved={isApproved}
              reviewStage={reviewStage}
              tableFilters={tableFilters}
              setTableFilters={setTableFilters}
            />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="bg-white dark:bg-darkBg rounded-lg shadow-sm border border-gray-200 dark:border-darkBg-border p-6 text-center py-12">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">Commission Reports</h3>
              <p className="text-gray-600 dark:text-darkBg-subText mb-6">Generate custom reports for your commission data</p>
              <div className="flex justify-center space-x-4">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                  Generate Monthly Report
                </button>
                <button className="px-4 py-2 border border-gray-300 dark:border-darkBg-border text-gray-700 dark:text-darkBg-mainText rounded-md hover:bg-gray-50 dark:hover:bg-darkBg-border">
                  Custom Report
                </button>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white dark:bg-darkBg rounded-lg shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">Commission Settings</h3>
              <div className="space-y-6 max-w-2xl">
                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-darkBg-mainText mb-2">Default Commission Rate</h4>
                  <p className="text-sm text-gray-600 dark:text-darkBg-subText mb-3">
                    This will be applied when no specific rate is set for a policy type
                  </p>
                  <div className="flex max-w-xs">
                    <input
                      type="number"
                      className="flex-1 rounded-l-md border-r-0 border-gray-300 dark:border-darkBg-border focus:ring-indigo-500 focus:border-indigo-500 dark:bg-darkBg dark:text-darkBg-mainText"
                      defaultValue="10"
                    />
                    <div className="inline-flex items-center px-4 rounded-r-md border border-l-0 border-gray-300 dark:border-darkBg-border bg-gray-50 dark:bg-darkBg-border text-gray-500 dark:text-darkBg-subText">
                      %
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-darkBg-border">
                  <h4 className="text-md font-medium text-gray-900 dark:text-darkBg-mainText mb-2">Commission Payment Schedule</h4>
                  <p className="text-sm text-gray-600 dark:text-darkBg-subText mb-3">
                    Define when commissions are paid out
                  </p>
                  <select className="block w-full max-w-xs border-gray-300 dark:border-darkBg-border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-darkBg dark:text-darkBg-mainText">
                    <option>Monthly (End of month)</option>
                    <option>Bi-weekly</option>
                    <option>Weekly</option>
                    <option>Upon policy activation</option>
                  </select>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-darkBg-border">
                  <h4 className="text-md font-medium text-gray-900 dark:text-darkBg-mainText mb-2">Notification Preferences</h4>
                  <p className="text-sm text-gray-600 dark:text-darkBg-subText mb-3">
                    Manage commission-related notifications
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="notify-commission-paid"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                        defaultChecked
                      />
                      <label htmlFor="notify-commission-paid" className="ml-2 text-sm text-gray-700 dark:text-darkBg-mainText">
                        Commission payment received
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="notify-rate-change"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                        defaultChecked
                      />
                      <label htmlFor="notify-rate-change" className="ml-2 text-sm text-gray-700 dark:text-darkBg-mainText">
                        Commission rate changes
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="notify-targets"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="notify-targets" className="ml-2 text-sm text-gray-700 dark:text-darkBg-mainText">
                        Commission target achievements
                      </label>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-darkBg-border flex justify-end">
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default CommissionsPage;